import apiClient from '../../../services/apiClient';

// Get the API base URL from the configured apiClient
const getApiBaseUrl = () => {
  return apiClient.defaults.baseURL;
};

class DoctorService {
  constructor() {
    this.baseURL = getApiBaseUrl();
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  // Get current user from localStorage
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Make authenticated API request
  async makeAuthenticatedRequest(url, options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const config = {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Could not validate credentials');
      }
      
      // Try to get detailed error message from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = Array.isArray(errorData.detail) 
            ? errorData.detail.map(err => err.msg || err).join(', ')
            : errorData.detail;
        }
      } catch (e) {
        // If we can't parse the error response, use the generic message
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Get doctor's tasks
  async getTasks() {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // For now, return mock data
      // In the future, this would call: `/api/v1/doctors/${user.id}/tasks`
      return [
        { id: 1, text: 'Review Medical For Sophie Hunter', checked: false },
        { id: 2, text: 'Follow-up results for Joseph Smith', checked: false },
        { id: 3, text: 'Review results for Aaron Rhodes', checked: false },
        { id: 4, text: 'Review results for Beth Smith', checked: false },
        { id: 5, text: 'Check Pathology Kelly Jones', checked: false }
      ];
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  // Get doctor's notifications
  async getNotifications() {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // For now, return mock data
      // In the future, this would call: `/api/v1/doctors/${user.id}/notifications`
      return [
        { id: 1, text: 'Booking Confirmation - Mary Jones', checked: false },
        { id: 2, text: 'Pathology Results - Bart Goodes', checked: false },
        { id: 3, text: 'Booking Confirmation - Joseph Smith', checked: false },
        { id: 4, text: 'Booking Confirmation - Sophie Smith', checked: false },
        { id: 5, text: 'Imaging Results - Sean Parker', checked: false },
        { id: 6, text: 'Pathology Results - Blake Baxter', checked: false },
        { id: 7, text: 'Booking Confirmation - Beverley Grey', checked: false }
      ];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Get doctor's appointments for today
  async getTodayAppointments() {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Return empty array - only real data from API should be displayed
      // TODO: Implement API call: `/api/v1/doctors/${user.id}/appointments/today`
      return [];
    } catch (error) {
      console.error('Error fetching today appointments:', error);
      throw error;
    }
  }

  // Get doctor bookings for a specific date
  async getDoctorBookings(doctorId, appointmentDate = null) {
    try {
      console.log('🔗 API Call - Doctor ID:', doctorId, 'Date:', appointmentDate);
      
      const params = new URLSearchParams();
      if (appointmentDate) {
        params.append('appointment_date', appointmentDate);
      }
      
      const endpoint = `/doctors/${doctorId}/bookings${params.toString() ? '?' + params.toString() : ''}`;
      console.log('🌐 API endpoint:', endpoint);
      
      const response = await apiClient.get(endpoint);
      const responseData = response.data;
      console.log('📡 Raw API response:', responseData);
      
      // Transform the backend booking data to match the frontend format
      const transformedBookings = responseData.map(booking => {
        console.log('🔄 Transforming booking:', booking);
        return {
          id: booking.id,
          time: booking.appointment_time ? booking.appointment_time.slice(0, 5) : '', // Convert "10:15:00" to "10:15"
          appointment_time: booking.appointment_time, // Keep full time format for compatibility
          date: booking.appointment_date,
          patientName: booking.patient_name || `Patient ${booking.patient_id}`, // Use actual patient name from joined query
          patient_name: booking.patient_name || `Patient ${booking.patient_id}`, // API-compatible field
          patient_id: booking.patient_id || 'Unknown', // String Patient ID from database (e.g., P123456)
          patient_phone: booking.patient_phone || 'N/A', // Patient phone from database
          patient_email: booking.patient_email || 'N/A', // Patient email from database
          reason: booking.reason || 'General Consultation',
          appointmentType: booking.appointment_type || 'Consultation',
          scheduleType: booking.schedule_type || 'In-Person',
          clinicLocation: booking.clinic_location || 'MediFusion Clinic',
          status: booking.status || 'Scheduled',
          // Keep legacy camelCase for backward compatibility
          patientEmail: booking.patient_email || '',
          patientPhone: booking.patient_phone || ''
        };
      });
      
      console.log('✅ Transformed bookings:', transformedBookings);
      return transformedBookings;
    } catch (error) {
      console.error('❌ Error fetching doctor bookings:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      // Return empty array on error instead of throwing to prevent UI crashes
      return [];
    }
  }

  // Availability APIs
  async getDoctorByUserId(userId) {
    const response = await apiClient.get(`/doctors/by-user/${userId}`);
    return response.data;
  }

  async getMonthAvailability(doctorNumericId, year, month) {
    const response = await apiClient.get(`/doctors/${doctorNumericId}/availability/month?year=${year}&month=${month}`);
    return response.data;
  }

  async getDaySlots(doctorNumericId, isoDate) {
    const response = await apiClient.get(`/doctors/${doctorNumericId}/availability/day/${isoDate}`);
    return response.data;
  }

  async bulkSetAvailability(doctorNumericId, payload) {
    const response = await apiClient.post(`/doctors/${doctorNumericId}/availability/bulk-days`, payload);
    return response.data;
  }

  async replaceDaySlots(doctorNumericId, isoDate, slots) {
    const response = await apiClient.post(`/doctors/${doctorNumericId}/availability/day/${isoDate}/slots`, { date: isoDate, slots });
    return response.data;
  }

  async setUnavailability(doctorNumericId, dates, reason) {
    const response = await apiClient.post(`/doctors/${doctorNumericId}/unavailability`, { dates, is_available: false, unavailability_reason: reason });
    return response.data;
  }

  // Get latest news
  async getLatestNews() {
    try {
      // For now, return mock data
      // In the future, this would call: `/api/v1/news/latest`
      return [
        {
          id: 1,
          image: '/news-thumbnail-1.jpg',
          title: 'News Title',
          excerpt: 'Paragraph Excerpt'
        },
        {
          id: 2,
          image: '/news-thumbnail-2.jpg',
          title: 'News Title',
          excerpt: 'Paragraph Excerpt'
        }
      ];
    } catch (error) {
      console.error('Error fetching latest news:', error);
      throw error;
    }
  }

  // Update task status
  async updateTaskStatus(taskId, checked) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // For now, just return success
      // In the future, this would call: `/api/v1/doctors/${user.id}/tasks/${taskId}`
      return { success: true };
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  }

  // Update notification status
  async updateNotificationStatus(notificationId, checked) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // For now, just return success
      // In the future, this would call: `/api/v1/doctors/${user.id}/notifications/${notificationId}`
      return { success: true };
    } catch (error) {
      console.error('Error updating notification status:', error);
      throw error;
    }
  }
}

export default new DoctorService();
